/**
 * Catálogo alternativo SAP (AlternateCatNum): Substitute del cliente → ItemCode.
 * POST `integration/sapb1/alternate-catalog/query`.
 */
import axiosInstance from '../utils/axiosInstance';
import { normalizeSapItemCodeKey, SAP_B1_ITEMS_BATCH_WAVE_SIZE } from './sapB1Items';

const TIMEOUT_MS = 120000;

/** Tanda del POST de catálogo. El backend vuelve a partir cada una para no pasar 2048 en la URL de SAP. */
export const SAP_B1_CATALOG_WAVE_SIZE = SAP_B1_ITEMS_BATCH_WAVE_SIZE;

function chunkArray(arr, size) {
  const n = Math.max(1, size);
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

function isCanceled(error, signal) {
  return Boolean(signal?.aborted || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED');
}

/**
 * Una llamada al backend con los Substitute de una tanda.
 *
 * @param {string} cardCode
 * @param {string[]} substitutes
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<object[]>}
 */
export async function fetchSapB1AlternateCatalog(cardCode, substitutes, options = {}) {
  const response = await axiosInstance.post(
    'integration/sapb1/alternate-catalog/query',
    { cardCode, substitutes },
    { signal: options.signal, timeout: TIMEOUT_MS }
  );
  const data = response?.data?.data;
  return Array.isArray(data) ? data : [];
}

/**
 * Todas las tandas en serie. Si una falla, sus Substitute no entran en el resultado
 * (la resolución los marca como no encontrados) y el resto sigue. Si fallan todas,
 * se relanza el último error. Un abort corta de inmediato.
 *
 * @param {string} cardCode
 * @param {string[]} substitutes
 * @param {{ signal?: AbortSignal, waveSize?: number, onProgress?: (p: { done: number, total: number }) => void }} [options]
 * @returns {Promise<object[]>}
 */
export async function fetchSapB1AlternateCatalogInWaves(cardCode, substitutes, options = {}) {
  const list = Array.isArray(substitutes) ? substitutes : [];
  const { signal, waveSize = SAP_B1_CATALOG_WAVE_SIZE, onProgress } = options;
  const waves = chunkArray(list, waveSize);
  const entries = [];
  let done = 0;
  let lastError = null;
  let failedWaves = 0;

  onProgress?.({ done: 0, total: list.length });
  for (const wave of waves) {
    if (signal?.aborted) {
      const abortError = new Error('canceled');
      abortError.name = 'CanceledError';
      abortError.code = 'ERR_CANCELED';
      throw abortError;
    }
    try {
      const rows = await fetchSapB1AlternateCatalog(cardCode, wave, { signal });
      entries.push(...rows);
    } catch (error) {
      if (isCanceled(error, signal)) throw error;
      lastError = error;
      failedWaves += 1;
    }
    done += wave.length;
    onProgress?.({ done, total: list.length });
  }

  if (failedWaves > 0 && failedWaves === waves.length && lastError) {
    throw lastError;
  }
  return entries;
}

/**
 * Índice Substitute normalizado → `{ itemCode }` | `{ ambiguous: true }` | `null`.
 *
 * @param {string[]} requestedSubstitutes
 * @param {object[]} entries filas devueltas por el backend
 */
export function buildCatalogResolution(requestedSubstitutes, entries) {
  /** @type {Map<string, Set<string>>} */
  const bySub = new Map();
  for (const entry of entries || []) {
    const sub = normalizeSapItemCodeKey(entry?.substitute ?? entry?.Substitute);
    const itemCode = normalizeSapItemCodeKey(entry?.itemCode ?? entry?.ItemCode);
    if (!sub || !itemCode) continue;
    if (!bySub.has(sub)) bySub.set(sub, new Set());
    bySub.get(sub).add(itemCode);
  }

  /** @type {Record<string, { itemCode?: string, ambiguous?: boolean }|null>} */
  const resolution = {};
  for (const raw of requestedSubstitutes || []) {
    const sub = normalizeSapItemCodeKey(raw);
    if (!sub || Object.prototype.hasOwnProperty.call(resolution, sub)) continue;
    const set = bySub.get(sub);
    if (!set || set.size === 0) {
      resolution[sub] = null;
    } else if (set.size > 1) {
      resolution[sub] = { ambiguous: true };
    } else {
      resolution[sub] = { itemCode: [...set][0] };
    }
  }
  return resolution;
}

/**
 * Mapa plano `Substitute → ItemCode` para enviar al backend en la importación
 * (`catalogItemCodes`). Solo entra lo resuelto a un único ítem: ausentes y ambiguos quedan fuera
 * (la vista previa ya los marca como error y bloquea la carga).
 *
 * @param {Record<string, { itemCode?: string, ambiguous?: boolean }|null>|null|undefined} resolution
 * @returns {Record<string, string>}
 */
export function buildCatalogItemCodeMap(resolution) {
  /** @type {Record<string, string>} */
  const out = {};
  if (!resolution || typeof resolution !== 'object') return out;
  for (const [sub, hit] of Object.entries(resolution)) {
    const key = normalizeSapItemCodeKey(sub);
    const itemCode = hit && !hit.ambiguous ? normalizeSapItemCodeKey(hit.itemCode) : '';
    if (key && itemCode) out[key] = itemCode;
  }
  return out;
}
